import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { inputClass } from "@/lib/constants";

const FIELDS = [
  ["name", "Business name", "text"],
  ["trading_name", "Trading name (if different)", "text"],
  ["owner_name", "Owner's full name", "text"],
  ["email", "Business email", "email"],
  ["phone", "Telephone", "tel"],
  ["public_address", "Public address / area", "text"],
  ["company_details", "Company or sole-trader details", "text"],
  ["domain", "Website domain", "text"],
  ["reply_to", "Email reply-to address", "email"],
];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [biz, setBiz] = useState({});
  const [socials, setSocials] = useState({ instagram: "", facebook: "", tiktok: "" });
  const [advanced, setAdvanced] = useState({ order_prefix: "", cutoff_hour: "18", cutoff_minute: "00" });
  const [confirmAdvanced, setConfirmAdvanced] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSettings(r.data);
      setBiz(r.data.business || {});
      setSocials({ instagram: "", facebook: "", tiktok: "", ...((r.data.business || {}).socials || {}) });
      const c = r.data.advanced?.cutoff || {};
      setAdvanced({
        order_prefix: r.data.advanced?.order_prefix || "BBCR",
        cutoff_hour: String(c.hour ?? 18).padStart(2, "0"),
        cutoff_minute: String(c.minute ?? 0).padStart(2, "0"),
      });
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = () => {
    const cutoffChanged = advanced.cutoff_hour !== String(settings?.advanced?.cutoff?.hour ?? 18).padStart(2, "0")
      || advanced.cutoff_minute !== String(settings?.advanced?.cutoff?.minute ?? 0).padStart(2, "0");
    const prefixChanged = advanced.order_prefix !== (settings?.advanced?.order_prefix || "BBCR");
    const advancedChanged = cutoffChanged || prefixChanged;
    if (advancedChanged && !confirmAdvanced) {
      toast.error("Advanced settings need the confirmation tick box before saving.");
      return;
    }
    api.put("/admin/settings", {
      business: { ...biz, socials },
      advanced: advancedChanged ? {
        order_prefix: advanced.order_prefix,
        cutoff: { weekday: 2, hour: parseInt(advanced.cutoff_hour, 10), minute: parseInt(advanced.cutoff_minute, 10) },
      } : {},
      confirm_advanced: advancedChanged && confirmAdvanced,
      base_updated_at: settings?.updated_at,
    }).then(() => {
      toast.success("Settings saved.");
      return api.get("/admin/settings").then((r) => setSettings(r.data));
    }).catch((e) => toast.error(formatApiError(e)));
  };

  if (!settings) return <p className="text-sm text-cream-muted">Loading…</p>;

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-serif text-2xl font-bold text-cream-text">Business Settings</h1>
      <p className="mt-1 text-sm text-cream-muted">These details appear on the website and in customer emails. Times always run on Europe/London; prices are in GBP.</p>

      <div className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map(([key, label, type]) => (
            <div key={key}>
              <label htmlFor={`biz-${key}`} className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">{label}</label>
              <input id={`biz-${key}`} type={type} value={biz[key] || ""} onChange={(e) => setBiz({ ...biz, [key]: e.target.value })}
                className={`${inputClass} mt-1 w-full`} data-testid={`settings-${key}`} />
            </div>
          ))}
        </div>
        <h2 className="mt-6 font-serif text-base font-semibold text-cream-text">Social links</h2>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {["instagram", "facebook", "tiktok"].map((net) => (
            <div key={net}>
              <label htmlFor={`social-${net}`} className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">{net}</label>
              <input id={`social-${net}`} type="url" value={socials[net] || ""} onChange={(e) => setSocials({ ...socials, [net]: e.target.value })}
                placeholder="https://…" className={`${inputClass} mt-1 w-full`} data-testid={`settings-social-${net}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-3xl border border-amber-500/40 bg-choco-light p-6" data-testid="advanced-settings-panel">
        <h2 className="font-serif text-lg font-semibold text-amber-200">Advanced — careful</h2>
        <p className="mt-1 text-xs text-cream-muted">These affect order numbers and the weekly ordering deadline. Wrong values can break ordering.</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="order-prefix" className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Order number prefix</label>
            <input id="order-prefix" value={advanced.order_prefix} onChange={(e) => setAdvanced({ ...advanced, order_prefix: e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 6) })}
              className={`${inputClass} mt-1 w-40`} data-testid="settings-order-prefix" />
          </div>
          <div>
            <span className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Weekly cut-off (Wednesday, Europe/London)</span>
            <div className="mt-1 flex items-center gap-2">
              <input aria-label="Cut-off hour" inputMode="numeric" value={advanced.cutoff_hour} onChange={(e) => setAdvanced({ ...advanced, cutoff_hour: e.target.value.replace(/\D/g, "").slice(0, 2) })}
                className={`${inputClass} w-20 text-center`} data-testid="settings-cutoff-hour" />
              <span className="text-cream-muted">:</span>
              <input aria-label="Cut-off minute" inputMode="numeric" value={advanced.cutoff_minute} onChange={(e) => setAdvanced({ ...advanced, cutoff_minute: e.target.value.replace(/\D/g, "").slice(0, 2) })}
                className={`${inputClass} w-20 text-center`} data-testid="settings-cutoff-minute" />
            </div>
          </div>
        </div>
        <label className="mt-4 flex min-h-[48px] items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
          <input type="checkbox" className="mt-0.5 h-5 w-5" checked={confirmAdvanced} onChange={(e) => setConfirmAdvanced(e.target.checked)} data-testid="advanced-confirm-checkbox" />
          I understand these changes affect live ordering and order numbering.
        </label>
      </div>

      <button onClick={save} className="mt-6 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-choco" data-testid="settings-save-button">
        <Save className="h-4 w-4" aria-hidden="true" /> Save settings
      </button>
    </div>
  );
}
